import React, { useEffect, useState } from 'react'

// Components
import SimpleScorecard from '../Game/ScoreCard'
import Scorecard from '../../components/Scorecard'
import { styles, Info, ScrollDownButton } from '../Game/Landing'
import {
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core'

// Util
import { generateHoles } from '../../util/scorecard'
import _ from 'lodash/fp'
import colors from '@fe/ui/colors'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

// Network/Storage
import { connect } from 'react-redux'
import * as OrganisationsReducer from '../../reducers/organisations'
import { getStaticBackground, RETURN_PATH, PATH_GRAPHQL_HTTP } from '../../config'
import ApolloClient from '../../apollo'
import { ApolloProvider, graphql } from 'react-apollo'
import { loader } from 'graphql.macro'
import { EmailIcon } from 'react-share'
const gameQuery = loader('./reviewGame.gql')

const withReviewData = connect(
  (s, ownProps) => {
    const reviewId = _.get('match.params.reviewId', ownProps)
    const oauthKey = _.get('match.params.oauthKey', ownProps)
    const unionId = _.get('match.params.unionId', ownProps)

    return {
      reviewId,
      oauthKey,
      unionId,
      submitError: OrganisationsReducer.selectors.submitError(s),
      submitSuccess: OrganisationsReducer.selectors.submitSuccess(s),
      submitRequestWaiting:
        OrganisationsReducer.selectors.submitRequestWaiting(s)
    }
  },
  (d, ownProps) => {
    const reviewId = _.get('match.params.reviewId', ownProps)
    const oauthKey = _.get('match.params.oauthKey', ownProps)
    const unionId = _.get('match.params.unionId', ownProps)
    return {
      submitReview: () =>
        d(
          OrganisationsReducer.actions.submitReview({
            oauthKey,
            reviewId,
            unionId
          })
        )
    }
  }
)

const getHeaderText = ({ loading, gameId }) => {
  if (loading) return 'Fetching Game'
  if (!gameId) return 'No game waiting for review'
  return 'Review Game'
}

const Header = (props) => (
  <>
    <Paper
      square
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Typography variant='h6'>{getHeaderText(props)}</Typography>
    </Paper>
    {Boolean(props.submitRequestWaiting) && (
      <Paper
        square
        style={{
          backgroundColor: colors.white,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LinearProgress style={{ width: '100%' }} />
      </Paper>
    )}
  </>
)

const StepperFlow = withReviewData((props) => {
  const {
    reviewId, // withReviewData
    oauthKey, // withReviewData
    unionId, // withReviewData
    submitReview, // withReviewData
    submitError, // withReviewData
    submitSuccess, // withReviewData
    submitRequestWaiting // withReviewData
  } = props
  const isSignedIn = !_.isEmpty(oauthKey)

  const stepLabel = submitError
    ? _.flow(_.get('message'), _.lowerCase, _.upperFirst)(submitError)
    : submitSuccess
      ? 'Confirmed!'
      : 'Confirm'

  const buttonHref =
    !isSignedIn && props.gameId
      ? `${PATH_GRAPHQL_HTTP}/review/oauth?unionID=${unionId}&mode=marker&gameId=${props.gameId}&reviewId=${reviewId}&returnTo=${RETURN_PATH}/review/${unionId}/${reviewId}`
      : undefined

  const [showDialog, setShowDialog] = useState(false)

  useEffect(() => {
    if (submitError) setShowDialog(true)
  }, [submitError])

  return (
    <div
      style={{
        width: '100%',
        padding: 0,
        position: 'sticky',
        bottom: 0
      }}
    >
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          An error has occurred
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <p>
              We are terribly sorry, but an error has occurred. Please try
              again. If the problem persists, please contact support.
            </p>

            <Button
              variant='contained'
              href='mailto:support@glfr.com'
              color='primary'
            >
              <EmailIcon
                size={32}
                bgStyle={{ backgroundColor: 'transparent', fill: '#326038' }}
              />
              Email Support
            </Button>

            <p>
              <a href='https://support.glfr.com'>
                Live chat with our support team on our website
              </a>
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} autoFocus>
            Understood
          </Button>
        </DialogActions>
      </Dialog>
      <Stepper
        alternativeLabel
        style={{
          width: '100%',
          padding: 10,
          display: 'flex',
          alignItems: 'center'
        }}
        activeStep={!isSignedIn ? 0 : 1}
      >
        <Step completed={isSignedIn}>
          <StepLabel>Sign In</StepLabel>
        </Step>
        <Step completed={Boolean(submitSuccess)}>
          <StepLabel error={Boolean(submitError)}>{stepLabel}</StepLabel>
        </Step>
        {!submitSuccess && (
          <Button
            onClick={
              !isSignedIn && props.gameId ? undefined : () => submitReview()
            }
            href={buttonHref}
            disabled={submitRequestWaiting}
            variant='contained'
            color='primary'
          >
            {!isSignedIn ? 'Sign In' : 'Confirm'}
          </Button>
        )}
      </Stepper>
    </div>
  )
})

const ReviewBase = (props) => {
  const {
    data: { gameByReviewId },
    classes,
    width,
    submitRequestWaiting // withReviewData
  } = props
  const gameId = _.get('gameId', gameByReviewId)
  const loading = _.get('data.loading', props)

  const canRender = Boolean(!loading && gameId)

  const guideTitle = _.get('game.guideTitle')(gameByReviewId)

  const clubName = _.get('game.clubName')(gameByReviewId)
  const club = { name: clubName, gallery: _.get('bannerUrl', gameByReviewId) }

  const gamePlayedAt = 1000 * _.getOr(0, 'game.gamePlayedAt')(gameByReviewId)
  const gameHandicap = _.get('game.gameHandicap', gameByReviewId)
  const ratingStyle = _.get('game.ratingStyle', gameByReviewId)

  const player = _.get('user', gameByReviewId)
  
  const holes = generateHoles(gameByReviewId, false, ratingStyle)

  const imgUrl = club.gallery || getStaticBackground()
  const image = `url('${imgUrl}')`
  const placing = 'no-repeat center center/cover'
  const gradient = isWidthUp('md', width)
    ? `linear-gradient(0deg, ${colors.primaryDarkGreen} 0%, rgba(255,255,255,0) 38%)`
    : `linear-gradient(0deg, ${colors.primaryDarkGreen} 10%, rgba(255,255,255,0) 50%)`

  return (
    <div>
      <div style={{ background: `${image} ${placing}`, height: '100%' }}>
        <div
          className={classes.background}
          style={{
            background: `${gradient}`,
            height: canRender ? 'calc(100vh - 80px)' : '100vh'
          }}
        >
          <Header
            loading={loading}
            gameId={gameId}
            submitRequestWaiting={submitRequestWaiting}
          />
          {canRender && (
            <>
              <Info
                club={club}
                date={gamePlayedAt}
                guide={{ title: guideTitle }}
                handicap={gameHandicap}
                player={player}
                classes={classes}
              />

              <div
                style={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end'
                }}
              >
                <SimpleScorecard holes={holes} big />
                {isWidthUp('md', width) && <ScrollDownButton />}
              </div>
            </>
          )}
        </div>
      </div>
      {canRender && (
        <div style={{ paddingBottom: 80, overflowX: 'auto' }}>
          <Scorecard holes={holes} />
        </div>
      )}
      {canRender && <StepperFlow match={props.match} gameId={gameId} />}
    </div>
  )
}

const Review = _.compose(
  withReviewData,
  withStyles(styles),
  withWidth(),
  graphql(gameQuery)
)(ReviewBase)

export default (props) => {
  return (
    <ApolloProvider client={ApolloClient}>
      <Review {...props} />
    </ApolloProvider>
  )
}
